if (!customElements.get("progress-bar")) {
  customElements.define(
    "progress-bar",
    class ProgressBar extends HTMLElement {
      // État persisté par block (clé = id du wrapper #shopify-block-…) : un
      // static unique serait écrasé par la dernière instance connectée si
      // plusieurs barres de progression coexistent dans le tiroir.
      static _stateByBlock = new Map();
      // Animation uniquement : page chargée panier vide, aucune instance n'a
      // capturé de total. Le premier ajout doit alors animer la barre depuis
      // 0 plutôt que l'afficher directement à sa largeur finale.
      static _emptyBaseline = false;
      // Synchronisation de l'attribut de panier des récompenses débloquées :
      // un seul POST /cart/update.js en vol pour tout le tiroir. Une barre
      // reconnectée pendant la requête se contente de laisser une demande,
      // rejouée à la fin du POST.
      static _syncInFlight = false;
      static _syncPending = false;
      // Dernière valeur acceptée par Shopify : si le re-rendu qui suit ne la
      // reflète pas (la suppression d'un attribut est peu fiable), on ne
      // reposte pas en boucle, on trace.
      static _lastSentUnlocked = null;

      _stateKey() {
        const wrapper = this.closest('[id^="shopify-block-"]');
        return wrapper ? wrapper.id : "progress-bar";
      }

      connectedCallback() {
        this.steps = this.querySelectorAll(".cart-drawer-progress-bar-advanced");
        this.wrapper = this.querySelector(".progress-bar__wrapper");
        this.dragEl = this.querySelector(".progress-bar__bar--drag");
        this.target = +this.dataset.target;
        this.cartTotal = +this.dataset.cartTotal || 0;
        this.allUnlockedMessage = this.dataset.allUnlockedMessage || "";
        this.titleEl = this.querySelector(".progress-bar__title");

        this.HORIZONTAL_BUFFER = 8;
        this._lastCartTotal = this.cartTotal;
        this._isFirstRun = true;

        if (!this.steps.length) {
          // Rien a animer, mais tout a synchroniser : sans etape la liste est
          // vide, et poster une liste vide est precisement ce qui nettoie un
          // panier portant encore une recompense.
          this.syncUnlockedGifts();
          return;
        }

        const C = this.constructor;
        const prev =
          C._stateByBlock.get(this._stateKey()) ||
          (C._emptyBaseline ? { cartTotal: 0, dragPercent: 0 } : null);
        // La base « panier vide » n'a servi qu'à ce premier connect.
        C._emptyBaseline = false;
        const animateOnConnect =
          !!prev && prev.cartTotal !== this.cartTotal && !!this.dragEl;

        if (animateOnConnect) {
          // The element is server-rendered with the NEW width and the
          // `.is-no-transition` class. We rewind it to the PREVIOUS width
          // without animation, flush layout, then drop the class so the
          // subsequent assignment to the new width plays the CSS transition.
          this._isInitialAnimating = true;

          this.dragEl.style.width = `${prev.dragPercent}%`;
          this.dragEl.offsetWidth;
          this.dragEl.classList.remove("is-no-transition");
          this._isFirstRun = false;

          this.updateTitle();
          this.applyMaxHeight();
          this.distributeAndFill({ animate: true });

          const endAnimation = () => {
            // transitionend et le repli minute peuvent tous deux arriver :
            // une seule fin d'animation.
            if (!this._isInitialAnimating) return;
            this._isInitialAnimating = false;
            if (this._animationFallbackTimer) {
              clearTimeout(this._animationFallbackTimer);
              this._animationFallbackTimer = null;
            }
            // La capture faite au connect a lieu avant la fin de la
            // transition : on re-capture ici la largeur réellement atteinte
            // pour que le prochain rembobinage parte de la bonne position.
            this._capturePersistedState();
            // La synchronisation attend la fin de l'animation : sa réponse
            // re-rend le tiroir, ce qui couperait net la transition.
            this.syncUnlockedGifts();
          };
          this.dragEl.addEventListener("transitionend", endAnimation, { once: true });
          this._animationFallbackTimer = setTimeout(endAnimation, 900);
        } else {
          this.distributeAndFill({ animate: false });
          this.updateTitle();
          this.applyMaxHeight();

          requestAnimationFrame(() => {
            if (this.dragEl) this.dragEl.classList.remove("is-no-transition");
            this._isFirstRun = false;
            this.syncUnlockedGifts();
          });
        }

        this._capturePersistedState();

        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        if (this.wrapper) this.resizeObserver.observe(this.wrapper);
        this.steps.forEach((step) => this.resizeObserver.observe(step));

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.scheduleLayout());
        }

        this.querySelectorAll(".cart-drawer-progress-bar-advanced img").forEach((img) => {
          if (!img.complete) {
            img.addEventListener("load", () => this.scheduleLayout(), { once: true });
          }
        });

        // Filet pour les mutations du panier qui ne re-rendent PAS le tiroir
        // (page /cart notamment) : le nouveau total est reflété tout de
        // suite, puis le tiroir est re-rendu depuis le serveur pour que la
        // liste des récompenses débloquées, calculée en Liquid, soit fraîche.
        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) =>
            this._onExternalCartUpdate(event)
          );
        }
      }

      _onExternalCartUpdate(event) {
        if (!event) return;
        const cart = event.cartData;
        // Seule la forme "panier complet" (réponses /cart/change|update) est
        // exploitable ; les réponses /cart/add (product-form) n'ont ni
        // total_price ni item_count, et ce chemin re-rend de toute façon le
        // tiroir (la synchronisation se fait alors au reconnect).
        if (
          !cart ||
          typeof cart.total_price !== "number" ||
          typeof cart.item_count !== "number"
        ) {
          return;
        }
        // Chemin quantité du tiroir : .drawer__inner est remplacé AVANT la
        // publication, la nouvelle instance porte déjà ce total.
        if (cart.total_price === this.cartTotal) return;

        this.cartTotal = cart.total_price;
        this.dataset.cartTotal = cart.total_price;
        this.distributeAndFill({ animate: false });
        this.updateTitle();
        // Re-capture la largeur réellement appliquée pour que la prochaine
        // reconnexion animée reparte de la bonne position.
        this._capturePersistedState();

        // Le calcul des récompenses débloquées vit en Liquid, une seule
        // implémentation : seul un rendu frais du tiroir le connaît. La barre
        // reconnectée synchronisera l'attribut. Jamais en mode design.
        if (window.Shopify && window.Shopify.designMode) return;
        this._refreshDrawerFromServer();
      }

      disconnectedCallback() {
        if (this._cartUpdateUnsubscriber) {
          this._cartUpdateUnsubscriber();
          this._cartUpdateUnsubscriber = null;
        }
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && cartDrawer.classList.contains("is-empty")) {
          // Panier vidé : oublier les totaux capturés et repartir d'une base
          // 0 pour que le premier ajout suivant anime la barre depuis 0.
          this.constructor._stateByBlock.clear();
          this.constructor._emptyBaseline = true;
        } else {
          this._capturePersistedState();
        }
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this._rafId) cancelAnimationFrame(this._rafId);
        if (this._animationFallbackTimer) clearTimeout(this._animationFallbackTimer);
      }

      _capturePersistedState() {
        if (!this.dragEl) return;
        let pct = null;
        const barEl = this.dragEl.parentElement;
        const barWidth = barEl ? barEl.getBoundingClientRect().width : 0;
        if (barWidth > 0) {
          pct = (this.dragEl.getBoundingClientRect().width / barWidth) * 100;
        } else {
          // Élément détaché (swap innerHTML / replaceWith) : les rects valent
          // 0. On retombe sur la largeur cible posée en style inline, qui est
          // toujours exprimée en pourcentage.
          const styleWidth = parseFloat(this.dragEl.style.width);
          if (Number.isFinite(styleWidth)) pct = styleWidth;
        }
        if (pct == null || !Number.isFinite(pct)) return;
        this.constructor._stateByBlock.set(this._stateKey(), {
          cartTotal: this.cartTotal,
          dragPercent: Math.max(0, Math.min(100, pct)),
        });
      }

      scheduleLayout() {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
          // While the mount-time transition is running, animate:true keeps
          // setBarWidth from temporarily adding `.is-no-transition`, which
          // would snap the in-flight CSS transition to its end value.
          const animate = !!this._isInitialAnimating;
          this.distributeAndFill({ animate });
          this.applyMaxHeight();
        });
      }

      setBarWidth(pct, animate) {
        if (!this.dragEl) return;
        const clamped = Math.max(0, Math.min(100, pct));

        if (!animate) {
          this.dragEl.classList.add("is-no-transition");
          this.dragEl.style.width = `${clamped}%`;
          this.dragEl.offsetWidth;
          if (!this._isFirstRun) {
            this.dragEl.classList.remove("is-no-transition");
          }
        } else {
          this.dragEl.style.width = `${clamped}%`;
        }
      }

      distributeAndFill({ animate = false } = {}) {
        if (!this.steps.length || !this.wrapper) return;

        const wrapperWidth = this.wrapper.getBoundingClientRect().width;
        if (wrapperWidth <= 0) return;

        const targetCents = this.target * 100;
        if (targetCents <= 0) return;

        Array.from(this.steps).forEach((step) => {
          step.style.maxWidth = "";
        });

        const stepData = Array.from(this.steps)
          .map((el) => {
            const dataEl = el.querySelector(".progress-bar__step-data");
            const amount = dataEl ? +dataEl.dataset.amount : 0;
            return { el, amount, width: el.offsetWidth };
          })
          .filter((s) => s.amount > 0)
          .sort((a, b) => a.amount - b.amount);

        if (!stepData.length) return;

        const positions = stepData.map((s) =>
          Math.min((s.amount / targetCents) * wrapperWidth, wrapperWidth)
        );

        for (let i = 1; i < positions.length; i++) {
          const minPos =
            positions[i - 1] +
            (stepData[i - 1].width + stepData[i].width) / 2 +
            this.HORIZONTAL_BUFFER;
          if (positions[i] < minPos) positions[i] = minPos;
        }

        const lastIdx = positions.length - 1;
        const rightEdge = wrapperWidth - stepData[lastIdx].width / 2;
        if (positions[lastIdx] > rightEdge) {
          positions[lastIdx] = rightEdge;
          for (let i = lastIdx - 1; i >= 0; i--) {
            const maxAllowed =
              positions[i + 1] -
              (stepData[i].width + stepData[i + 1].width) / 2 -
              this.HORIZONTAL_BUFFER;
            if (positions[i] > maxAllowed) positions[i] = maxAllowed;
          }
        }

        if (positions[0] < stepData[0].width / 2) {
          positions[0] = stepData[0].width / 2;
          for (let i = 1; i < positions.length; i++) {
            const minPos =
              positions[i - 1] +
              (stepData[i - 1].width + stepData[i].width) / 2 +
              this.HORIZONTAL_BUFFER;
            if (positions[i] < minPos) positions[i] = minPos;
          }
        }

        stepData.forEach((s, i) => {
          const pct = (positions[i] / wrapperWidth) * 100;
          s.el.style.left = `${pct}%`;
          s.visualPct = pct;
        });

        this.updateBarFill(stepData, targetCents, animate);
      }

      updateBarFill(stepData, targetCents, animate) {
        if (!this.dragEl) return;

        const lastAmount = stepData[stepData.length - 1].amount;

        if (this.cartTotal >= lastAmount) {
          this.setBarWidth(100, animate);
          return;
        }

        const points = [{ amount: 0, pct: 0 }];
        stepData.forEach((s) => points.push({ amount: s.amount, pct: s.visualPct }));
        if (lastAmount < targetCents) {
          points.push({ amount: targetCents, pct: 100 });
        }

        let pct = 0;
        for (let i = 0; i < points.length - 1; i++) {
          if (this.cartTotal >= points[i].amount && this.cartTotal <= points[i + 1].amount) {
            const span = points[i + 1].amount - points[i].amount;
            const t = span > 0 ? (this.cartTotal - points[i].amount) / span : 0;
            pct = points[i].pct + t * (points[i + 1].pct - points[i].pct);
            break;
          }
        }

        this.setBarWidth(pct, animate);
      }

      updateTitle() {
        if (!this.titleEl) return;

        const stepsData = Array.from(this.steps)
          .map((step) => {
            const data = step.querySelector(".progress-bar__step-data");
            if (!data) return null;
            return {
              amount: +(data.dataset.amount || 0),
              remaining: data.dataset.remaining || ""
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.amount - b.amount);

        let nextStep = null;
        stepsData.forEach((step) => {
          if (this.cartTotal < step.amount && !nextStep) {
            nextStep = step;
          }
        });

        if (nextStep) {
          this.titleEl.innerHTML = nextStep.remaining;
        } else if (stepsData.length > 0) {
          this.titleEl.innerHTML = this.allUnlockedMessage;
        }
      }

      applyMaxHeight() {
        if (!this.wrapper || !this.steps.length) return;

        const stepOffset = this.steps[0].offsetTop;
        const maxStepHeight = Math.max(
          ...Array.from(this.steps).map((el) => el.offsetHeight)
        );

        this.wrapper.style.height = `${stepOffset + maxStepHeight}px`;
      }

      // Union lue sur `data-unlocked-desired` (toutes les barres, y compris
      // celles qui ne postent pas) et non sur le marqueur d'election.
      // Union des récompenses débloquées de toutes les barres du document,
      // dédoublonnée par variante (1er champ), dans l'ordre d'apparition.
      // L'attribut de panier est unique : avec deux barres, chacune ne voit
      // que ses propres étapes, mais toutes doivent s'accorder sur une seule
      // valeur, sinon elles se repostent l'une l'autre sans fin.
      static _mergedDesired() {
        const seen = new Set();
        const out = [];
        const add = (value) => {
          (value || "")
            .split("|")
            .filter(Boolean)
            .forEach((entry) => {
              const id = entry.split(":")[0];
              if (!seen.has(id)) {
                seen.add(id);
                out.push(entry);
              }
            });
        };
        // Source de vérité : chaque ÉTAPE signe sa propre récompense
        // (cart-drawer-progress-bar-item-advanced). Le bloc parent devait pour
        // cela parcourir `block.blocks`, propriété que Shopify ne garantit pas
        // sur un bloc de thème imbriqué : quand elle est vide, les étapes
        // s'affichent — `content_for 'blocks'` est un chemin indépendant — mais
        // plus rien n'est signé, et l'encart Cadeaux reste vide sans raison
        // apparente. Lire les étapes supprime cette dépendance.
        document
          .querySelectorAll("progress-bar [data-unlocked-entry]")
          .forEach((el) => add(el.dataset.unlockedEntry));
        // Repli : une barre rendue par une version antérieure du thème porte
        // encore la liste sur elle-même. Le dédoublonnage par variante rend les
        // deux sources cumulables sans risque de doublon.
        document
          .querySelectorAll("progress-bar[data-unlocked-desired]")
          .forEach((el) => add(el.dataset.unlockedDesired));
        return out.join("|");
      }

      syncUnlockedGifts(isRetry) {
        // Aligne l'attribut de panier `_unlocked_gifts` sur la liste des
        // récompenses débloquées calculée en Liquid par le bloc parent
        // (data-unlocked-desired). Le JS ne calcule rien : il compare et
        // poste. C'est l'application (Worker) qui, à la commande, vérifie la
        // signature et ajoute la récompense offerte.
        //
        // Jamais en mode design : le panier du marchand serait réellement
        // modifié depuis l'éditeur de thème.
        if (window.Shopify && window.Shopify.designMode) return;
        // Attribut absent : le bloc parent ne gère pas les récompenses, rien à
        // faire. Chaîne VIDE : valeur à part entière — le réglage « Ajouter les
        // récompenses à la commande » est sur Non, ou aucun palier n'est
        // franchi, et la poster efface la récompense d'un panier qui en portait
        // encore une. Ne jamais confondre les deux.
        if (this.dataset.unlockedDesired === undefined) return;
        if (typeof window.routes === "undefined" || !window.routes.cart_update_url) return;
        if (typeof fetchConfig !== "function") return;
        // Seule la première barre du document poste, pour le compte de toutes.
        // Elle doit etre une barre qui synchronise vraiment : une barre encore
        // en cours de connexion, elue par sa seule presence dans le DOM,
        // laisserait toutes les autres se retirer et personne ne posterait.
        this.dataset.unlockedSync = "";
        if (document.querySelector("progress-bar[data-unlocked-sync]") !== this) return;

        const C = this.constructor;
        const desired = C._mergedDesired();
        // Transport sans JS au clic « Commander » : les inputs cachés portent
        // la même valeur que celle envoyée ici, quel que soit le nombre de barres.
        document
          .querySelectorAll('input[name="attributes[_unlocked_gifts]"]')
          .forEach((input) => {
            input.value = desired;
          });

        const current = this.dataset.unlockedCurrent || "";
        if (desired === current) {
          C._lastSentUnlocked = null;
          return;
        }
        if (C._syncInFlight) {
          C._syncPending = true;
          return;
        }
        if (C._lastSentUnlocked === desired) {
          console.warn(
            "[progress-bar] attribut _unlocked_gifts accepté par Shopify mais non reflété après re-rendu : nouvel envoi abandonné"
          );
          return;
        }

        C._syncInFlight = true;
        const body = JSON.stringify({
          attributes: { _unlocked_gifts: desired },
          // Sections re-rendues renvoyées avec la réponse : elles permettent
          // de rafraîchir tout le tiroir (encart Cadeaux, barre, totaux) en
          // un seul aller-retour.
          sections: "cart-drawer-advanced,cart-icon-bubble",
          sections_url: window.location.pathname,
        });

        // Instantané du jeton de rendu : si un rendu plus récent du tiroir
        // survient pendant le vol du POST, sa réponse est périmée et ne doit
        // pas être appliquée telle quelle.
        const seqAtPost = window.__cartDrawerRenderSeq || 0;

        fetch(window.routes.cart_update_url, { ...fetchConfig(), body })
          .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
          .then(({ ok, data }) => {
            if (!ok) {
              throw new Error(
                (data && (data.description || data.message)) || `HTTP ${JSON.stringify(data)}`
              );
            }
            // Mémorisé seulement une fois la valeur acceptée : posé avant le
            // POST, une 429 figerait la synchronisation jusqu'au rechargement.
            C._lastSentUnlocked = desired;
            const isFresh = seqAtPost === (window.__cartDrawerRenderSeq || 0);
            const rendered = isFresh && this._renderDrawerSections(data);
            if (!rendered) {
              // Soit le tiroir a été re-rendu pendant le vol du POST (nos
              // sections sont un instantané antérieur), soit la réponse ne
              // contenait pas de sections exploitables : on re-fetch l'état
              // frais pour ne jamais laisser le tiroir obsolète.
              this._refreshDrawerFromServer();
            }
          })
          .catch((e) => {
            // 429 fréquent juste après une mutation du panier, ou coupure
            // réseau : un unique retry différé, puis on laisse le prochain
            // rendu du tiroir retenter.
            C._lastSentUnlocked = null;
            console.error("[progress-bar] synchronisation des récompenses débloquées :", e);
            if (!isRetry) {
              setTimeout(() => {
                if (this.isConnected) this.syncUnlockedGifts(true);
              }, 1000);
            }
          })
          .finally(() => {
            C._syncInFlight = false;
            if (C._syncPending) {
              // Une barre reconnectée pendant le vol a laissé une demande :
              // c'est elle, désormais première du document, qui reprend.
              C._syncPending = false;
              const bar = document.querySelector("progress-bar[data-unlocked-sync]");
              if (bar && bar !== this && typeof bar.syncUnlockedGifts === "function") {
                bar.syncUnlockedGifts();
              }
            }
          });
      }

      _renderDrawerSections(data) {
        // Retourne true seulement si le tiroir a réellement été re-rendu ;
        // l'appelant retombe sinon sur un re-fetch de l'état serveur.
        const sections = data && data.sections;
        if (!sections || !sections["cart-drawer-advanced"]) return false;

        const doc = new DOMParser().parseFromString(
          sections["cart-drawer-advanced"],
          "text/html"
        );
        const drawerEl = document.querySelector("cart-drawer");
        const inner = drawerEl && drawerEl.querySelector(".drawer__inner");
        const newInner = doc.querySelector(".drawer__inner");
        if (!inner || !newInner) return false;

        // Invalide les rafraîchissements du tiroir (GET de onCartUpdate)
        // partis avant cet état : leur réponse serait périmée. Uniquement
        // maintenant que le swap est certain, pour ne pas tuer un GET
        // correctif si on avait dû abandonner.
        window.__cartDrawerRenderSeq = (window.__cartDrawerRenderSeq || 0) + 1;

        // La classe is-empty de l'hôte pilote le layout, le conteneur de
        // trapFocus à l'ouverture ET la remise à zéro de l'état persisté
        // dans le disconnectedCallback des <progress-bar> détruites par le
        // swap : elle doit être posée AVANT l'affectation innerHTML (même
        // ordre que CartItems.updateQuantity), d'après l'état serveur rendu.
        const newDrawerHost = doc.querySelector("cart-drawer");
        if (newDrawerHost) {
          drawerEl.classList.toggle(
            "is-empty",
            newDrawerHost.classList.contains("is-empty")
          );
        }

        const focusWasInside = inner.contains(document.activeElement);
        inner.innerHTML = newInner.innerHTML;

        // Le swap détruit l'élément focalisé s'il était dans le tiroir : sans
        // re-trap, le bouclage clavier du dialogue pointe des nœuds détachés.
        // On ne vole pas le focus s'il était ailleurs (saisie hors tiroir).
        if (
          focusWasInside &&
          drawerEl.classList.contains("active") &&
          typeof trapFocus === "function"
        ) {
          const container = document.getElementById("CartDrawer");
          if (container) trapFocus(container, inner);
        }

        const bubble = document.getElementById("cart-icon-bubble");
        if (bubble && sections["cart-icon-bubble"]) {
          const bubbleDoc = new DOMParser().parseFromString(
            sections["cart-icon-bubble"],
            "text/html"
          );
          const src = bubbleDoc.querySelector(".shopify-section");
          if (src) bubble.innerHTML = src.innerHTML;
        }
        return true;
      }

      _refreshDrawerFromServer(isRetry) {
        // Repli : re-rend le tiroir ET la pastille du panier depuis l'état
        // serveur courant. Utilisé après une mutation du panier qui n'a pas
        // re-rendu le tiroir (page /cart), ou quand le rendu direct des
        // sections de la réponse /cart/update.js est impossible ou périmé.
        // Un unique retry couvre le throttling (429) fréquent juste après
        // un POST sur le panier.
        //
        // Le jeton est INCRÉMENTÉ à l'émission, comme dans cart.js : le
        // dernier GET parti gagne, les précédents s'auto-invalident. Un
        // simple instantané laissait deux GET concurrents appliquer le plus
        // ancien en dernier, et le tiroir sur un panier périmé.
        if (typeof window.routes === "undefined" || !window.routes.cart_url) return;
        const seq = (window.__cartDrawerRenderSeq = (window.__cartDrawerRenderSeq || 0) + 1);
        fetch(`${window.routes.cart_url}?sections=cart-drawer-advanced,cart-icon-bubble`)
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          })
          .then((sections) => {
            if (seq !== (window.__cartDrawerRenderSeq || 0)) return;
            this._renderDrawerSections({ sections });
          })
          .catch((e) => {
            console.error("[progress-bar] rafraîchissement du tiroir impossible :", e);
            if (!isRetry) {
              setTimeout(() => this._refreshDrawerFromServer(true), 1000);
            }
          });
      }
    }
  );
}

// Panier vide au chargement de la page : aucune <progress-bar> n'est rendue
// (le tiroir affiche l'état "sans produit"), donc aucun connect ne capture de
// total de départ. On amorce la base à 0 pour que le premier ajout anime la
// barre depuis 0.
(() => {
  const drawer = document.querySelector("cart-drawer");
  const ProgressBarClass = customElements.get("progress-bar");
  if (drawer && drawer.classList.contains("is-empty") && ProgressBarClass) {
    ProgressBarClass._emptyBaseline = true;
  }
})();
